import { MouseEvent, RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import parse from 'html-react-parser';

import { bridgeAtom, renderedColorsAtom, sessionAtom, useSetValue, useValue } from '@/store';

import { addProductToCart, addShopifyEvent } from '@/modules/api';
import { Quantity, Spinner, Variants } from '@/modules/common';
import { EventType, LinkType } from '@/modules/models';
import { Variant } from '@/modules/models/Variant';
import { VariantType } from '@/modules/models/VariantType';

import {
  Card,
  CheckItOut,
  ErrorText,
  ImageContainer,
  ProductButton,
  ProductCardWrapper,
  ProductInfo,
  ProductPrice,
  ProductTitle,
} from './ProductCard.styles';

const toLowerCase = (array: string[]) => array.map((item) => item.toLowerCase());

export const ProductCard = ({
  product,
  bubbleRef,
}: {
  product: LinkType;
  bubbleRef: RefObject<HTMLDivElement | null>;
}) => {
  const { variants, optionNames, handle } = product;

  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const [selectedValues, setSelectedValues] = useState<(string | undefined)[]>(() =>
    Array.from({ length: optionNames?.length ?? 0 })
  );
  const [price, setPrice] = useState<string | null>(null);
  const [image, setImage] = useState<string | null>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<string>('1');

  const { translations, onAddToCartFromChat, shopifyAddToCartParams, productCard } = useValue(bridgeAtom);
  const session = useValue(sessionAtom);
  const setRenderedColors = useSetValue(renderedColorsAtom);

  const { disabled, hideAddToCartButton, hideVariants = true, variantsConfig } = productCard || {};

  const colorOptionNames = useMemo(() => toLowerCase(variantsConfig?.color?.optionNames || []), [variantsConfig]);
  const dropdownOptionNames = useMemo(() => toLowerCase(variantsConfig?.dropdown?.optionNames || []), [variantsConfig]);
  const buttonOptionNames = useMemo(() => toLowerCase(variantsConfig?.button?.optionNames || []), [variantsConfig]);

  const reset = useCallback(() => {
    setError(null);

    if (variants.length > 1) {
      setSelectedValues([]);
    }
  }, [variants]);

  const selectOption = useCallback(
    (value: string, index: number) => {
      setSelectedValues((prevState) => {
        const newState = [...prevState];
        newState[index] = value;
        return newState;
      });
      setError(null);
    },
    [setSelectedValues]
  );

  const getAllValuesByIndex = useCallback(
    (optionIndex: number): string[] => {
      const valuesSet = new Set<string>();

      for (const variant of variants as Variant[]) {
        const value = variant.options_values[optionIndex];
        if (value) {
          valuesSet.add(value);
        }
      }

      return Array.from(valuesSet);
    },
    [variants]
  );

  const getOptionType = useCallback(
    (optionName: string) => {
      switch (true) {
        case colorOptionNames.includes(optionName):
          return VariantType.COLOR;

        case buttonOptionNames.includes(optionName):
          return VariantType.BUTTON;

        case dropdownOptionNames.includes(optionName):
          return VariantType.DROPDOWN;

        default:
          return null;
      }
    },
    [dropdownOptionNames, colorOptionNames, buttonOptionNames]
  );

  const getConfigByName = useCallback(
    (originalOptionName: string) => {
      const lowerOptionName = originalOptionName.toLowerCase();
      const type = getOptionType(lowerOptionName);

      const replaceName = type && variantsConfig?.[type]?.replaceName;
      const labelText = replaceName ? translations.variants.label(replaceName) : originalOptionName;

      return {
        labelText,
        type,
        originalOptionName,
        replaceName,
      };
    },
    [variantsConfig, getOptionType, translations.variants]
  );

  const optionsList = useMemo(() => {
    return optionNames?.filter(Boolean).map((option, index) => ({
      ...getConfigByName(option as string),
      values: getAllValuesByIndex(index),
      value: selectedValues[index],
    }));
  }, [optionNames, getConfigByName, getAllValuesByIndex, selectedValues]);

  const validate = useCallback(() => {
    let isValid = true;

    if (variants.length === 1) {
      return true;
    }

    const emptyList = optionsList
      ?.map(({ value, replaceName, originalOptionName }) => !Boolean(value) && (replaceName ?? originalOptionName))
      .filter(Boolean);

    if (emptyList?.length) {
      setError(translations.variants.emptySelectError(emptyList as string[]));
      return false;
    }

    if (!selectedVariant) {
      return false;
    }

    return isValid;
  }, [variants, selectedVariant, optionsList, translations.variants]);

  const onCheckItOut = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      const element = event.target as HTMLAnchorElement;
      const url = element.href;

      event.preventDefault();

      try {
        await addShopifyEvent({
          userSession: session,
          type: EventType.OPEN_PRODUCT_FROM_CHAT,
          handle,
          quantity: 1,
        });
      } catch (error) {
        console.log('Error check it out event', error);
      }

      window.location.href = url;
    },
    [session, handle]
  );

  const addToCart = useCallback(async () => {
    const isValid = validate();

    if (!isValid) {
      return;
    }

    setIsLoading(true);
    try {
      const { variant_id } = selectedVariant || {};

      if (!variant_id) {
        throw Error('Invalid variant');
      }

      const product = await addProductToCart(
        handle,
        variant_id,
        parseInt(quantity, 10),
        session,
        shopifyAddToCartParams
      );
      setIsLoading(false);
      onAddToCartFromChat?.(product);
      reset();
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      setError(translations.errors.addToCartFailure);
    }
  }, [
    validate,
    selectedVariant,
    session,
    shopifyAddToCartParams,
    onAddToCartFromChat,
    reset,
    translations.errors,
    quantity,
    handle,
  ]);

  useEffect(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    if (variants?.length === 1) {
      setSelectedVariant(variants[0]);
    }
  }, [variants]);

  useEffect(() => {
    setImage(product.imageUrl);
    setPrice(product.price);
  }, [product]);

  useEffect(() => {
    if (selectedValues.filter(Boolean).length === optionNames?.filter(Boolean)?.length) {
      const variant = variants.find(
        ({ options_values }) =>
          JSON.stringify(options_values.filter(Boolean)) === JSON.stringify(selectedValues.filter(Boolean))
      );
      setSelectedVariant(variant || null);
    }
  }, [selectedValues, optionNames, variants]);

  useEffect(() => {
    if (!selectedVariant) {
      return;
    }

    setPrice(selectedVariant.price);
    setImage(selectedVariant.image_url);
  }, [selectedVariant]);

  useEffect(() => {
    const hasColors = optionsList?.filter(({ type }) => type === 'color').length;
    if (!hasColors) {
      setRenderedColors((prev) => ({
        ...prev,
        [handle]: true,
      }));
    }
  }, [handle, optionsList, setRenderedColors]);

  const hasVariants = Boolean(variants?.length > 1);

  const isVariationsHidden = hideVariants || hideAddToCartButton || !hasVariants;
  const isAddToCartHidden = hideAddToCartButton || (hideVariants && hasVariants);

  if (disabled) {
    return null;
  }

  return (
    <ProductCardWrapper>
      <Card>
        <ProductInfo>
          <ProductTitle>{product?.title}</ProductTitle>
          {price && <ProductPrice>{price}</ProductPrice>}
          {product?.url && (
            <CheckItOut href={product?.url} onClick={onCheckItOut} target="_blank">
              Check it out
            </CheckItOut>
          )}
        </ProductInfo>

        <ImageContainer>{image && <img src={image} alt={product?.title as string} />}</ImageContainer>
      </Card>

      {!isVariationsHidden && (
        <Variants
          handle={handle}
          selectedValues={selectedValues}
          optionsList={optionsList}
          selectOption={selectOption}
          bubbleRef={bubbleRef}
        />
      )}

      {!isAddToCartHidden && <Quantity value={quantity} changeValue={setQuantity} />}

      {error && <ErrorText>{error}</ErrorText>}

      {!isAddToCartHidden && (
        <ProductButton onClick={addToCart}>
          {isLoading ? <Spinner color="black" /> : parse(translations.addToCartButton)}
        </ProductButton>
      )}
    </ProductCardWrapper>
  );
};
